import { CodeBlock } from "@/features/docs/components/CodeBlock";
import {
  InlineCode,
  MCP_URL,
  MethodBadge,
  Note,
  P,
  Section,
  Step,
  docTextLinkClass,
} from "@/features/docs/components/DocPrimitives";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const CLIENTS = [
  { id: "install-claude-code", label: "Claude Code (terminal)" },
  { id: "install-claude-desktop", label: "Claude Desktop & claude.ai" },
  { id: "install-codex", label: "Codex" },
  { id: "install-chatgpt", label: "ChatGPT" },
  { id: "install-cursor", label: "Cursor" },
  { id: "install-windsurf", label: "Windsurf" },
  { id: "install-antigravity", label: "Antigravity" },
  { id: "install-windows", label: "Windows" },
  { id: "install-other", label: "Other MCP clients" },
];

const scrollTo = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "instant", block: "start" });
};

const Prompt = ({ title, children }: { title: string; children: string }) => {
  return (
    <div className="mb-5">
      <h3 className="mb-2 text-base font-medium text-zinc-900">{title}</h3>
      <blockquote className="border-l border-zinc-300 pl-4 text-base leading-7 text-zinc-600">
        {children}
      </blockquote>
    </div>
  );
};

export const InstallContent = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      scrollTo(location.hash.slice(1));
    }
  }, [location.hash]);

  return (
    <>
      <h2 className="font-display mb-4 text-3xl tracking-tight text-zinc-900">
        Install by client
      </h2>
      <P>
        Step-by-step setup for every supported app. Clients marked{" "}
        <span className="text-2xs uppercase tracking-[0.1em] text-zinc-500">Local install</span>{" "}
        support the full <InlineCode>paperclip</InlineCode> command set; all clients can
        connect to the hosted MCP server. See the{" "}
        <Link to="/docs" className={docTextLinkClass}>main docs</Link> for what Paperclip
        can do once connected.
      </P>
      <Note>
        After a local CLI install, <InlineCode>paperclip install</InlineCode> adds the core
        agent skill. Guided workflows are enabled separately on your account with{" "}
        <InlineCode>paperclip routines list</InlineCode> and{" "}
        <InlineCode>paperclip routines enable &lt;name&gt;</InlineCode>; enabling one does not
        write workflow files into the project.
      </Note>

      <nav aria-label="Installation clients" className="mb-12 mt-7 flex flex-wrap gap-x-5 gap-y-2">
        {CLIENTS.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => scrollTo(c.id)}
            className="text-sm text-zinc-500 underline decoration-zinc-300 underline-offset-4 transition-colors hover:text-zinc-900 hover:decoration-zinc-600"
          >
            {c.label}
          </button>
        ))}
      </nav>

      <div className="space-y-12">
        <Section id="install-claude-code" title="Claude Code (terminal)">
          <MethodBadge kind="both" />
          <P>
            Claude Code supports both connection methods. The local install is recommended —
            it gives the agent the full <InlineCode>paperclip</InlineCode> command set
            (pipes, grep across the corpus, repos).
          </P>
          {/* TODO: add video — <VideoPlaceholder title="Install Paperclip in Claude Code" /> */}
          <h3 className="text-base font-medium text-zinc-900 mb-3">Option A — Local install (recommended)</h3>
          <Step n={1}>
            <P>Run the installer, then sign in when prompted:</P>
            <CodeBlock code={`curl -fsSL https://paperclip.gxl.ai/install.sh | bash`} language="bash" title="your terminal" />
          </Step>
          <Step n={2}>
            <P>Teach your agent the commands — install the skill file into your project, selecting <strong>Claude Code</strong>:</P>
            <CodeBlock code={`paperclip install`} language="bash" title="your terminal" />
          </Step>
          <Step n={3}>
            <P>Start a new Claude Code session (or run <InlineCode>/reload</InlineCode>), then mention <InlineCode>/paperclip</InlineCode> in a prompt.</P>
          </Step>
          <h3 className="text-base font-medium text-zinc-900 mb-3 mt-6">Option B — MCP server</h3>
          <Step n={1}>
            <P>Add the server from your terminal:</P>
            <CodeBlock code={`claude mcp add --transport http paperclip ${MCP_URL}`} language="bash" title="your terminal" />
          </Step>
          <Step n={2}>
            <P>Start <InlineCode>claude</InlineCode>, enter <InlineCode>/mcp</InlineCode>, and select <strong>Authenticate</strong> under the paperclip server.</P>
          </Step>
        </Section>

        <Section id="install-claude-desktop" title="Claude Desktop, claude.ai & Cowork">
          <MethodBadge kind="mcp" />
          <P>
            Claude Desktop, claude.ai web, and Claude Cowork all connect to Paperclip
            through <strong>custom connectors</strong> — no local install required. A
            Paperclip listing in Claude’s Connectors Directory is coming soon. In the meantime,
            feel free to create your own custom connector:
          </P>
          {/* TODO: add video — <VideoPlaceholder title="Add Paperclip to Claude Desktop" /> */}
          <Step n={1}>
            <P>
              Open <strong>Customize → Connectors</strong>. (If you go to{" "}
              <strong>Settings → Connectors</strong>, it points you to Customize —
              connectors are managed there now.)
            </P>
          </Step>
          <Step n={2}>
            <P>Click the <strong>+</strong> button and choose <strong>Add custom connector</strong>.</P>
          </Step>
          <Step n={3}>
            <P>
              Name it <InlineCode>Paperclip</InlineCode> and paste the{" "}
              <strong>Remote MCP server URL</strong>, then click <strong>Add</strong>.
              Leave the Advanced OAuth fields empty.
            </P>
            <CodeBlock code={MCP_URL} language="text" title="Claude app — Add custom connector" />
          </Step>
          <Step n={4}>
            <P>
              Select Paperclip in the connector list and click <strong>Connect</strong>
              to complete the sign-in in your browser. Sign in with your Paperclip account.
              Once you are signed in, Paperclip’s tools will appear in
              new chats. Allow pop-ups for Claude if the window does not open.
            </P>
          </Step>
          <Note>
            Don't add remote servers to <InlineCode>claude_desktop_config.json</InlineCode> —
            that file only supports local stdio processes. Custom connectors require a
            Pro, Max, Team, or Enterprise plan.
          </Note>

          <h3 className="mb-3 mt-6 text-base font-medium text-zinc-900">Tools</h3>
          <P>
            Claude’s Paperclip tools are the Paperclip commands, one tool per command:{" "}
            <InlineCode>search</InlineCode>, <InlineCode>cat</InlineCode>,{" "}
            <InlineCode>lookup</InlineCode>, <InlineCode>share</InlineCode>, and the rest
            of the command set. The{" "}
            <Link to="/docs" className={docTextLinkClass}>
              command reference
            </Link>{" "}
            describes what each one does.
          </P>
          <P>
            The connector can import a file attached to a Claude message into your
            Clipboard. If your organization is on a Team or Enterprise plan, an organization
            admin may need to open <strong>Organization settings → Capabilities</strong> and add{" "}
            <InlineCode>storage.googleapis.com</InlineCode> to the code-execution domain
            allowlist to prevent file uploads being blocked.
          </P>

          <h3 className="mb-3 mt-6 text-base font-medium text-zinc-900">Example prompts</h3>
          <P>Try these once Paperclip is connected.</P>
          <Prompt title="Search">
            Use Paperclip to find recent papers on base-editing delivery to the liver. List
            the top 5 with title, year, and journal.
          </Prompt>
          <Prompt title="Read">
            Open the full text of PMC8820186 in Paperclip and summarize its main
            conclusions about the cardiovascular benefits of GLP-1 receptor agonists, in a
            few bullet points.
          </Prompt>
          <Prompt title="Import">
            Import this attached file into my Paperclip clipboard and confirm the path it
            was saved to.
          </Prompt>

          <h3 className="mb-3 mt-6 text-base font-medium text-zinc-900">Troubleshooting</h3>
          <P>
            If Paperclip’s tools disappear, open <strong>Customize → Connectors</strong>,
            select Paperclip, and click <strong>Connect</strong> again.
          </P>
          <P>
            If the sign-in window never appears, allow pop-ups for Claude and try{" "}
            <strong>Connect</strong> again.
          </P>
          <P>
            For anything else, email{" "}
            <a href="mailto:support@gxl.ai" className={docTextLinkClass}>
              support@gxl.ai
            </a>
            .
          </P>

          <h3 className="mb-3 mt-6 text-base font-medium text-zinc-900">Privacy and support</h3>
          <P>
            <a
              href="https://gxl.ai/privacy-notice"
              target="_blank"
              rel="noopener noreferrer"
              className={docTextLinkClass}
            >
              Privacy notice
            </a>
            . Support:{" "}
            <a href="mailto:support@gxl.ai" className={docTextLinkClass}>
              support@gxl.ai
            </a>
            .
          </P>
        </Section>

        <Section id="install-codex" title="Codex (CLI & Desktop)">
          <MethodBadge kind="both" />
          <P>
            The Codex CLI and the Codex desktop app share the same configuration
            (<InlineCode>~/.codex/config.toml</InlineCode>) — set up either one and both work.
          </P>
          {/* TODO: add video — <VideoPlaceholder title="Install Paperclip in Codex" /> */}
          <h3 className="text-base font-medium text-zinc-900 mb-3">Option A — Local install</h3>
          <Step n={1}>
            <P>Run the installer, then sign in when prompted:</P>
            <CodeBlock code={`curl -fsSL https://paperclip.gxl.ai/install.sh | bash`} language="bash" title="your terminal" />
          </Step>
          <Step n={2}>
            <P>Install the skill into your project (writes <InlineCode>.agents/skills/paperclip/SKILL.md</InlineCode>):</P>
            <CodeBlock code={`paperclip install   # select Codex`} language="bash" title="your terminal" />
          </Step>
          <Step n={3}>
            <P>Start a new Codex session and mention <InlineCode>/paperclip</InlineCode> in a prompt.</P>
          </Step>
          <h3 className="text-base font-medium text-zinc-900 mb-3 mt-6">Option B — MCP server (desktop app)</h3>
          <Step n={1}>
            <P>In Settings, open <strong>MCP servers</strong> in the sidebar and choose <strong>Connect to a custom MCP</strong>.</P>
          </Step>
          <Step n={2}>
            <P>Name it <InlineCode>paperclip</InlineCode>, select the <strong>Streamable HTTP</strong> tab, and paste the server URL:</P>
            <CodeBlock code={MCP_URL} language="text" title="Codex app — Settings → MCP servers" />
          </Step>
          <Step n={3}>
            <P>
              Under <strong>Headers</strong>, add a header with key{" "}
              <InlineCode>X-API-Key</InlineCode> and an API key created at{" "}
              <Link to="/settings/keys" className={docTextLinkClass}>
                paperclip.gxl.ai/keys
              </Link>{" "}
              as the value, then click <strong>Save</strong>.
            </P>
            <P>
              Prefer browser sign-in instead? Save without headers, then run{" "}
              <InlineCode>codex mcp login paperclip</InlineCode> in your terminal — the
              desktop app and CLI share the same config.
            </P>
          </Step>
          <h3 className="text-base font-medium text-zinc-900 mb-3 mt-6">Option C — MCP server (CLI)</h3>
          <Step n={1}>
            <P>Register the server:</P>
            <CodeBlock code={`codex mcp add paperclip --url ${MCP_URL}`} language="bash" title="your terminal" />
          </Step>
          <Step n={2}>
            <P>Authenticate (opens your browser):</P>
            <CodeBlock code={`codex mcp login paperclip`} language="bash" title="your terminal" />
          </Step>
          <Step n={3}>
            <P>Verify with <InlineCode>codex mcp list</InlineCode> — paperclip should show as connected. The desktop app shares the same config.</P>
          </Step>
          <Note>
            Codex supports streamable-HTTP MCP servers only (no legacy SSE). If tools
            don't appear after login, update to the latest Codex version.
          </Note>
        </Section>

        <Section id="install-chatgpt" title="ChatGPT">
          <MethodBadge kind="mcp" />
          <P>
            ChatGPT (chatgpt.com) connects to Paperclip as a <strong>custom connector</strong>.
            This requires <strong>Developer mode</strong>, available on paid plans on the web.
            Once linked, the connector also works in the ChatGPT mobile apps.
          </P>
          {/* TODO: add video — <VideoPlaceholder title="Add Paperclip to ChatGPT" /> */}
          <Step n={1}>
            <P>
              Enable Developer mode: <strong>Settings → Apps → Advanced settings</strong> →
              toggle <strong>Developer mode</strong>.
            </P>
          </Step>
          <Step n={2}>
            <P>
              Back in <strong>Apps</strong>, click <strong>Create app</strong> (this button
              only appears with Developer mode on). In the <strong>New App</strong> dialog,
              name it <InlineCode>Paperclip</InlineCode>, keep <strong>Connection</strong> on{" "}
              <strong>Server URL</strong>, paste the MCP server URL, and leave{" "}
              <strong>Authentication</strong> as <strong>OAuth</strong>:
            </P>
            <CodeBlock code={MCP_URL} language="text" title="ChatGPT — New App dialog" />
          </Step>
          <Step n={3}>
            <P>
              Check <strong>"I understand and want to continue"</strong> under the custom-server
              risk notice, then click <strong>Create</strong> and complete the sign-in in your
              browser. If the connection succeeds, ChatGPT lists the tools Paperclip advertises.
            </P>
          </Step>
          <Step n={4}>
            <P>
              In a new chat, add the Paperclip connector to the conversation (via the{" "}
              <strong>+</strong> / tools menu in the composer) and ask away.
            </P>
          </Step>
        </Section>

        <Section id="install-cursor" title="Cursor">
          <MethodBadge kind="both" />
          {/* TODO: add video — <VideoPlaceholder title="Install Paperclip in Cursor" /> */}
          <h3 className="text-base font-medium text-zinc-900 mb-3">Option A — Local install (recommended)</h3>
          <Step n={1}>
            <P>Run the installer, then sign in when prompted:</P>
            <CodeBlock code={`curl -fsSL https://paperclip.gxl.ai/install.sh | bash`} language="bash" title="your terminal" />
          </Step>
          <Step n={2}>
            <P>Teach your agent the commands — install the skill file into your project:</P>
            <CodeBlock code={`paperclip install   # select Cursor`} language="bash" title="your terminal" />
          </Step>
          <Step n={3}>
            <P>Reload the window (<InlineCode>Cmd/Ctrl + Shift + P</InlineCode> → <strong>Reload Window</strong>) and mention <InlineCode>/paperclip</InlineCode> in a prompt.</P>
          </Step>
          <h3 className="text-base font-medium text-zinc-900 mb-3 mt-6">Option B — MCP server</h3>
          <Step n={1}>
            <P>
              Open <strong>Cursor Settings</strong> (<InlineCode>Cmd/Ctrl + Shift + J</InlineCode>) →{" "}
              <strong>Tools &amp; MCPs</strong> → <strong>New MCP Server</strong> (shown as{" "}
              <strong>Add Custom MCP</strong> when the list is empty). Cursor opens{" "}
              <InlineCode>~/.cursor/mcp.json</InlineCode> for you — add:
            </P>
            <CodeBlock
              code={`{\n  "mcpServers": {\n    "paperclip": {\n      "url": "${MCP_URL}",\n      "type": "http"\n    }\n  }\n}`}
              language="json"
              title="~/.cursor/mcp.json"
            />
            <P>
              To share the server with your team instead, create{" "}
              <InlineCode>.cursor/mcp.json</InlineCode> in the project root with the same content.
            </P>
          </Step>
          <Step n={2}>
            <P>
              Back in <strong>Tools &amp; MCPs</strong>, find paperclip under{" "}
              <strong>Installed MCP Servers</strong> and sign in (Cursor opens your
              browser). Once connected it shows <strong>Logout</strong> and the enabled
              tool count.
            </P>
          </Step>
        </Section>

        <Section id="install-windsurf" title="Windsurf">
          <MethodBadge kind="mcp" />
          <P>
            Connect Cascade via the hosted MCP server.
          </P>
          {/* TODO: add video — <VideoPlaceholder title="Add Paperclip to Windsurf" /> */}
          <Step n={1}>
            <P>
              Open <strong>Windsurf Settings</strong> → <strong>Cascade</strong> tab. Under{" "}
              <strong>MCP Servers</strong>, click <strong>Open MCP Registry</strong>.
            </P>
          </Step>
          <Step n={2}>
            <P>
              Create an API key at{" "}
              <Link to="/settings/keys" className={docTextLinkClass}>
                paperclip.gxl.ai/keys
              </Link>
              . Then, in the registry, click the <strong>gear icon</strong> (next to{" "}
              <strong>Installed</strong>) to open{" "}
              <InlineCode>~/.codeium/windsurf/mcp_config.json</InlineCode> in the editor, and add:
            </P>
            <CodeBlock
              code={`{\n  "mcpServers": {\n    "paperclip": {\n      "serverUrl": "${MCP_URL}",\n      "headers": {\n        "X-API-Key": "gxl_..."\n      }\n    }\n  }\n}`}
              language="json"
              title="~/.codeium/windsurf/mcp_config.json"
            />
          </Step>
          <Step n={3}>
            <P>
              Save, then <strong>refresh</strong> the MCP server list in Cascade. Paperclip's
              tools are now available to the agent.
            </P>
          </Step>
          <Note>
            Windsurf also supports browser OAuth (omit <InlineCode>headers</InlineCode>),
            but the sign-in prompt only triggers on the first tool call and is easily lost
            to popup blockers — an API key is the reliable path. If you try OAuth and it
            stalls, clear the cache with <InlineCode>rm -rf ~/.mcp-auth</InlineCode> and
            restart Windsurf.
          </Note>
        </Section>

        <Section id="install-antigravity" title="Antigravity (App & IDE)">
          <MethodBadge kind="mcp" />
          <P>
            Works in both the standalone Antigravity app and the Antigravity IDE — they
            share the same MCP configuration. The <strong>Add MCP +</strong> button only
            lists Google's pre-built catalog, so custom servers like             Paperclip are added
            via the config file.
          </P>
          {/* TODO: add video — <VideoPlaceholder title="Add Paperclip to Antigravity" /> */}
          <P>
            First, create an API key at{" "}
            <Link to="/settings/keys" className={docTextLinkClass}>
              paperclip.gxl.ai/keys
            </Link>
            , then follow the steps for the surface you're using. Both read the same
            config, and the entry to add is:
          </P>
          <CodeBlock
            code={`{\n  "mcpServers": {\n    "paperclip": {\n      "serverUrl": "${MCP_URL}",\n      "headers": {\n        "X-API-Key": "gxl_..."\n      }\n    }\n  }\n}`}
            language="json"
            title="~/.gemini/config/mcp_config.json"
          />
          <Note>
            Antigravity requires <InlineCode>serverUrl</InlineCode> — not{" "}
            <InlineCode>url</InlineCode> — for remote servers, and the JSON cannot contain
            comments.
          </Note>
          <h3 className="text-base font-medium text-zinc-900 mb-3 mt-6">Standalone app</h3>
          <Step n={1}>
            <P>
              The app has no built-in editor, so open the config in any editor from your
              terminal and add the entry above:
            </P>
            <CodeBlock
              code={`mkdir -p ~/.gemini/config\nopen -e ~/.gemini/config/mcp_config.json   # macOS TextEdit (or: code ...)`}
              language="bash"
              title="your terminal"
            />
          </Step>
          <Step n={2}>
            <P>
              In the app, open <strong>Settings</strong> (gear, bottom-left) →{" "}
              <strong>Customizations</strong> and click <strong>Refresh</strong> next to{" "}
              <strong>Installed MCP Servers</strong>. Paperclip appears with its tools
              enabled.
            </P>
          </Step>
          <h3 className="text-base font-medium text-zinc-900 mb-3 mt-6">IDE</h3>
          <Step n={1}>
            <P>
              Click the <strong>…</strong> menu at the top of the agent panel →{" "}
              <strong>MCP Servers</strong> → <strong>Manage MCP Servers</strong> →{" "}
              <strong>View raw config</strong>. The IDE opens{" "}
              <InlineCode>mcp_config.json</InlineCode> in the editor — add the entry above
              and save.
            </P>
          </Step>
          <Step n={2}>
            <P>
              Back in <strong>Manage MCP Servers</strong>, click <strong>Refresh</strong>.
              Paperclip's tools become available to the agent.
            </P>
          </Step>
        </Section>

        <Section id="install-windows" title="Windows">
          <MethodBadge kind="mcp" />
          <P>
            On Windows, the <strong>preferred method is to use the apps or websites
              listed above</strong> (Claude Desktop, claude.ai, ChatGPT, Cursor, etc.) —
            MCP connections work natively on Windows, so follow those sections as-is.
            The local CLI isn't supported on native Windows: the installer is a bash
            script and the CLI's interactive prompts use Unix terminal APIs.
          </P>
        </Section>

        <Section id="install-other" title="Other MCP clients">
          <MethodBadge kind="mcp" />
          <P>
            Paperclip works with any client that supports remote MCP servers over{" "}
            <strong>streamable HTTP</strong> with OAuth sign-in (Zed, Continue, and others).
            Point your client at:
          </P>
          <CodeBlock code={MCP_URL} language="text" title="MCP server URL" />
          <P>
            Most clients accept this as an "HTTP" or "remote" MCP server and open a
            browser window to authenticate on first use. If your client supports
            custom headers instead of OAuth, see{" "}
            <Link to="/settings/keys" className={docTextLinkClass}>
              API keys
            </Link>{" "}
            for header-based authentication.
          </P>
        </Section>
      </div>
    </>
  );
};

export const InstallPage = () => {
  useDocumentTitle("Install");

  return (
    <div className="h-full overflow-y-auto">
      <div className="editorial-frame">
        <InstallContent />
      </div>
    </div>
  );
};
